import dayjs from 'dayjs';
import { parse } from 'exifr';
import { isNil } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { GameRound, GameToEdit } from './types';

export const checkForChanges = (
  gameToEdit: GameToEdit,
  name: string,
  publicGame: boolean,
  rounds: GameRound[]
): boolean => {
  if (gameToEdit.name !== name) return true;
  if (gameToEdit.publicGame !== publicGame) return true;

  for (let i = 0; i < gameToEdit.rounds.length; i++) {
    const oldRound = gameToEdit.rounds[i];
    const newRound = rounds[i];
    if (checkForRoundChanges(oldRound, newRound)) {
      return true;
    }
  }

  return false;
};

const checkForRoundChanges = (
  oldRound: GameRound,
  newRound: GameRound
): boolean => {
  return (
    newRound.image instanceof File ||
    (!isNil(oldRound.location) &&
      !isNil(newRound.location) &&
      (oldRound.location.latitude !== newRound.location.latitude ||
        oldRound.location.longitude !== newRound.location.longitude)) ||
    (isNil(oldRound.location) && !isNil(newRound.location)) ||
    oldRound.year !== newRound.year ||
    oldRound.description !== newRound.description ||
    oldRound.photographer !== newRound.photographer
  );
};

interface ImageMetadata {
  year: string;
  location: mapkit.Coordinate | null;
}

export const getImageMetadata = async (image: File): Promise<ImageMetadata> => {
  const metadata: ImageMetadata = {
    year: '',
    location: null,
  };

  try {
    const output = await parse(image, { gps: true });
    const dateTaken = output.DateTimeOriginal;
    const latitude = output.latitude;
    const longitude = output.longitude;

    if (
      !isNil(latitude) &&
      !isNil(longitude) &&
      typeof latitude === 'number' &&
      typeof longitude === 'number' &&
      !isNaN(latitude) &&
      !isNaN(longitude)
    ) {
      metadata.location = {
        latitude: latitude,
        longitude: longitude,
      } as mapkit.Coordinate;
    }

    if (!isNil(dateTaken)) {
      metadata.year = dayjs(dateTaken).year().toString();
    }
  } catch (error) {
    console.log('Error parsing EXIF data:', error);
    metadata.year = '';
    metadata.location = null;
  }

  return metadata;
};

export function usePersistedState<S>(
  initialValue: S,
  callback: () => void
): [S, Dispatch<SetStateAction<S>>, (v: S) => void] {
  const [value, setValue] = useState<S>(initialValue);
  const [shouldRunCallback, setShouldRunCallback] = useState<boolean>(false);

  const setValueAndPersist = useCallback(
    (v: S) => {
      setValue(v);
      setShouldRunCallback(true);
    },
    [setValue, setShouldRunCallback]
  );

  useEffect(() => {
    if (shouldRunCallback) {
      setShouldRunCallback(false);
      callback();
    }
  }, [shouldRunCallback, callback]);

  return [value, setValue, setValueAndPersist];
}
